import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddressSearch from '../components/AddressSearch';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash, MagnifyingGlass, FolderOpen, Clock, MapPin, ShareNetwork, Warning, X } from '@phosphor-icons/react';
import { getCapabilities } from '../lib/planFeatures';

export default function ProjectsPage() {
  const { apiCall, user: currentUser } = useAuth();
  const capabilities = getCapabilities(currentUser);
  const canShare = !!capabilities.isPro; // Pro and Enterprise can share; Basic cannot
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', address: '' });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareProject, setShareProject] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuggestions, setShareSuggestions] = useState([]);
  const [showShareSuggestions, setShowShareSuggestions] = useState(false);
  const [planLimits, setPlanLimits] = useState(null);
  const shareDebounceRef = React.useRef(null);
  const shareWrapperRef = React.useRef(null);

  useEffect(() => { loadProjects(); loadPlanLimits(); }, []);

  // Quick Look hand-off: when navigated here with `?address=…` we open the
  // New Project dialog pre-filled with that address. Strip the param after
  // applying so a refresh doesn't keep reopening the dialog.
  useEffect(() => {
    const addr = searchParams.get('address');
    if (!addr) return;
    setEditProject(null);
    setForm({ name: '', description: '', address: addr });
    setShowDialog(true);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Quick action intent from CommandPalette
  useEffect(() => {
    const intent = sessionStorage.getItem('roofmate_pending_action');
    if (intent === 'new-project') {
      sessionStorage.removeItem('roofmate_pending_action');
      setTimeout(() => openNew(), 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close share suggestions on click outside
  useEffect(() => {
    function handleClick(e) {
      if (shareWrapperRef.current && !shareWrapperRef.current.contains(e.target)) {
        setShowShareSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function loadProjects() {
    try {
      const res = await apiCall('get', '/projects');
      setProjects(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function loadPlanLimits() {
    if (!currentUser?.tenant_id) return;
    try {
      const res = await apiCall('get', `/billing/check-limits/${currentUser.tenant_id}`);
      setPlanLimits(res.data);
    } catch (e) {
      // Plan limits drive the upgrade banner; failing fetch should not crash the page.
      console.warn('loadPlanLimits failed:', e);
    }
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    try {
      // Carry the Google place_id when available so future Quick Look lookups
      // can find this project by stable Place ID rather than fuzzy address.
      const mapData = { address: form.address };
      if (selectedLocation?.place_id) mapData.place_id = selectedLocation.place_id;
      if (editProject) {
        await apiCall('put', `/projects/${editProject.id}`, {
          name: form.name,
          description: form.description,
          map_data: mapData,
        });
      } else {
        const location = selectedLocation
          ? { lat: selectedLocation.lat, lng: selectedLocation.lng, zoom: 17 }
          : { lat: 51.5074, lng: -0.1278, zoom: 15 };
        await apiCall('post', '/projects', {
          name: form.name,
          description: form.description,
          location,
          map_data: mapData,
        });
      }
      setShowDialog(false);
      setEditProject(null);
      setForm({ name: '', description: '', address: '' });
      setSelectedLocation(null);
      loadProjects();
    } catch (e) { console.error(e); }
  }

  async function handleDelete(id) {
    try {
      await apiCall('delete', `/projects/${id}`);
      setDeleteTarget(null);
      loadProjects();
    } catch (e) { console.error(e); }
  }

  function openEdit(p) {
    setEditProject(p);
    setForm({
      name: p.name,
      description: p.description || '',
      address: p.map_data?.address || ''
    });
    setSelectedLocation(null);
    setShowDialog(true);
  }

  function openNew() {
    setEditProject(null);
    setForm({ name: '', description: '', address: '' });
    setSelectedLocation(null);
    setShowDialog(true);
  }

  function handleAddressSelect(loc) {
    setSelectedLocation(loc);
    setForm(prev => ({ ...prev, address: loc.address }));
  }

  async function openShare(p) {
    setShareProject(p); setShareEmail(''); setShareLoading(false); setShareError(''); setShareSuggestions([]);
    try {
      const res = await apiCall('get', `/projects/${p.id}/shared-users`);
      setSharedUsers(res.data || []);
    } catch { setSharedUsers([]); }
  }

  async function searchShareUsers(q) {
    if (!q || q.length < 1) { setShareSuggestions([]); return; }
    try {
      const res = await apiCall('get', `/users/search?q=${encodeURIComponent(q)}`);
      setShareSuggestions(res.data || []);
      setShowShareSuggestions(true);
    } catch { setShareSuggestions([]); }
  }

  function handleShareEmailChange(val) {
    setShareEmail(val); setShareError('');
    if (shareDebounceRef.current) clearTimeout(shareDebounceRef.current);
    shareDebounceRef.current = setTimeout(() => searchShareUsers(val), 300);
  }

  function selectShareUser(u) {
    setShareEmail(u.email);
    setShowShareSuggestions(false);
    setShareSuggestions([]);
  }

  async function handleShare() {
    if (!shareEmail.trim() || !shareProject) return;
    // Client-side self-share guard — saves a round-trip and produces a
    // friendlier error than the backend 400. The backend also blocks this
    // case so a hand-crafted POST can't bypass it.
    if (shareEmail.trim().toLowerCase() === (currentUser?.email || '').toLowerCase()) {
      setShareError("You can't share a project with yourself.");
      return;
    }
    setShareLoading(true); setShareError('');
    try {
      await apiCall('post', `/projects/${shareProject.id}/share`, { user_email: shareEmail });
      setShareEmail('');
      const res = await apiCall('get', `/projects/${shareProject.id}/shared-users`);
      setSharedUsers(res.data || []);
      loadProjects();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to share project';
      if (detail.includes('not found')) {
        setShareError(`No user found with email "${shareEmail}". Make sure the email is correct and the user belongs to your team.`);
      } else {
        setShareError(detail);
      }
    } finally { setShareLoading(false); }
  }

  async function handleUnshare(userId) {
    if (!shareProject) return;
    try {
      await apiCall('delete', `/projects/${shareProject.id}/share/${userId}`);
      const res = await apiCall('get', `/projects/${shareProject.id}/shared-users`);
      setSharedUsers(res.data || []);
      // Refresh the projects list so the "shared" badge / coloured share icon
      // in the row updates immediately when the last user is unshared — the
      // server already wrote shared_with=[] on the project, we just need to
      // re-pull it for the table state.
      loadProjects();
    } catch (e) { console.error(e); }
  }

  // Broad search — matches across project name, address, created-by user
  // name, measurement label / tool / subtype, and the formatted created
  // date. The "matched" field name is captured so the row can render a small
  // "matched: <field>" hint chip explaining why the row was kept.
  //
  // We compute the filter inline (not memoised) because the project list is
  // small (<1000 rows on enterprise tenants) and the search runs on every
  // keystroke anyway — premature memoisation would just complicate the
  // matched-field tracking. If a tenant ever exceeds ~5k projects we should
  // move this to a backend ?q= query instead.
  const q = search.trim().toLowerCase();
  const filtered = q ? projects
    .map((p) => {
      // First match wins — order matters here, more specific fields come last
      // so the hint chip surfaces the most useful provenance.
      if ((p.name || '').toLowerCase().includes(q)) return { project: p, matched: 'name' };
      const addr = (p.map_data?.address || p.description || '').toLowerCase();
      if (addr && addr.includes(q)) return { project: p, matched: 'address' };
      if ((p.created_by || '').toLowerCase().includes(q)) return { project: p, matched: 'created by' };
      const dateStr = p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB').toLowerCase() : '';
      if (dateStr && dateStr.includes(q)) return { project: p, matched: 'date' };
      // Measurements: label OR measurement_type OR shape_type/subtype. A
      // measurement_type like "site_crane" or "polygon" should match a user
      // typing "crane" / "polygon" / "site". `replaceAll('_', ' ')` so
      // `site_crane` matches a query of just "crane".
      const ms = Array.isArray(p.measurements) ? p.measurements : [];
      for (const m of ms) {
        const fields = [
          m.label,
          m.measurement_type,
          m.measurement_type ? String(m.measurement_type).replace(/_/g, ' ') : '',
          m.shape_type,
          m.subtype,
        ];
        for (const f of fields) {
          if (f && String(f).toLowerCase().includes(q)) {
            return { project: p, matched: m.measurement_type === 'site_logistics' ? 'site logistic' : 'measurement' };
          }
        }
      }
      return null;
    })
    .filter(Boolean) : projects.map((p) => ({ project: p, matched: null }));

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="projects-heading">
            Projects
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {projects.length} total projects
            {planLimits && planLimits.limits.max_projects < 999999 && (
              <span className="ml-2 text-xs">({planLimits.usage.projects}/{planLimits.limits.max_projects} used)</span>
            )}
          </p>
        </div>
        {!currentUser?.is_locked && (
          <Button onClick={openNew} className="rounded-md font-medium" disabled={planLimits && !planLimits.can_create_project} data-testid="create-project-btn">
            <Plus size={16} className="mr-2" weight="bold" /> New Project
          </Button>
        )}
      </div>

      {/* Plan limit warning */}
      {planLimits && !planLimits.can_create_project && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm" data-testid="project-limit-warning">
          <Warning size={18} weight="bold" className="shrink-0" />
          <span>Project limit reached ({planLimits.usage.projects}/{planLimits.limits.max_projects}). <a href={currentUser?.role === 'tenant_admin' ? '/my-billing' : '#'} className="underline font-semibold">Upgrade your plan</a> to create more projects.</span>
        </div>
      )}

      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, address, owner, tool (e.g. crane, polygon)…"
          className="pl-9 pr-9 rounded h-10"
          data-testid="projects-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Clear search"
            aria-label="Clear search"
            data-testid="projects-search-clear"
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card className="shadow-card rounded-lg border-0">
          <CardContent className="p-12 text-center text-muted-foreground">
            <FolderOpen size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-semibold text-lg">No projects found</p>
            <p className="text-sm mt-1">Create a new project to get started with roof measurements.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-card rounded-lg border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Address</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Measurements</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created By</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created Date</TableHead>
                <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(({ project: p, matched }) => (
                <TableRow key={p.id} className="hover:bg-secondary/30 cursor-pointer" data-testid={`project-row-${p.id}`}>
                  <TableCell className="font-medium">
                    <button onClick={() => navigate(`/project/${p.id}`)} className="hover:text-primary transition-colors flex items-center gap-2">
                      <MapPin size={14} className="text-primary" />
                      <span>{p.name}</span>
                      {/* Show why this row matched the broad search — only
                          when a query is active AND the match wasn't the
                          obvious "name" case (the name itself is already in
                          this cell). */}
                      {matched && matched !== 'name' && (
                        <span
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary font-bold"
                          title={`Matched on ${matched}`}
                          data-testid={`project-row-${p.id}-matched`}
                        >
                          {matched}
                        </span>
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                    {p.map_data?.address || p.description || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {(p.measurements || []).length}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.created_by || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canShare && (currentUser?.role === 'tenant_admin' || String(p.user_id) === currentUser?.id) && (
                        <Button size="sm" variant="ghost" onClick={() => openShare(p)}
                          className={`h-8 w-8 p-0 ${(p.shared_with && p.shared_with.length > 0) ? 'text-primary' : ''}`}
                          title={p.shared_with?.length ? `Shared with ${p.shared_with.length} user(s)` : 'Share'}
                          data-testid={`share-project-${p.id}`}>
                          <ShareNetwork size={14} weight={(p.shared_with && p.shared_with.length > 0) ? 'fill' : 'regular'} />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="h-8 w-8 p-0" data-testid={`edit-project-${p.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(p)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" data-testid={`delete-project-${p.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editProject ? 'Edit Project' : 'New Project'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Name</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Project name" className="rounded-md" data-testid="dialog-project-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Project description..."
                className="rounded min-h-[80px] resize-y"
                rows={3}
                data-testid="dialog-project-desc"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium">Address</Label>
              <AddressSearch
                value={form.address}
                onChange={(val) => setForm(p => ({ ...p, address: val }))}
                onSelect={handleAddressSelect}
                placeholder="Search for an address..."
              />
              {selectedLocation && (
                <p className="text-xs text-primary flex items-center gap-1">
                  <MapPin size={12} weight="bold" />
                  Location found: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} className="rounded-md" data-testid="dialog-save-btn">{editProject ? 'Save' : 'Create'}</Button>
            <Button variant="outline" onClick={() => { setShowDialog(false); setSelectedLocation(null); }} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Project"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? All measurements, layers, and map data for this project will be permanently removed. This action cannot be undone.`}
        onConfirm={() => handleDelete(deleteTarget?.id)}
        destructiveLabel="Delete Project"
      />

      {/* Share Dialog */}
      <Dialog open={!!shareProject} onOpenChange={(open) => { if (!open) { setShareProject(null); setShareError(''); } }}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle><ShareNetwork size={20} className="inline mr-2 text-primary" />Share Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Share "{shareProject?.name}" with team members.</p>
            {shareError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400" data-testid="share-error">
                {shareError}
              </div>
            )}
            <div className="relative" ref={shareWrapperRef}>
              <div className="flex gap-2">
                <Input
                  type="text" value={shareEmail}
                  onChange={e => handleShareEmailChange(e.target.value)}
                  onFocus={() => shareSuggestions.length > 0 && setShowShareSuggestions(true)}
                  placeholder="Search by name or email..."
                  className="flex-1"
                  onKeyDown={e => { if (e.key === 'Enter') { setShowShareSuggestions(false); handleShare(); } if (e.key === 'Escape') setShowShareSuggestions(false); }}
                  data-testid="share-email-input"
                  autoComplete="off"
                />
                <Button onClick={handleShare} disabled={shareLoading || !shareEmail.trim()} className="shrink-0" data-testid="share-submit-btn">
                  {shareLoading ? '...' : 'Share'}
                </Button>
              </div>
              {showShareSuggestions && shareSuggestions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="share-suggestions">
                  {shareSuggestions.map(u => (
                    <button key={u.id} onClick={() => selectShareUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary/80 transition-colors flex items-center gap-2 border-b border-border/30 last:border-0"
                      data-testid={`share-suggest-${u.id}`}>
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold shrink-0">
                        {(u.name || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {sharedUsers.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Shared with:</p>
                {sharedUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-secondary/30 text-sm">
                    <span>{u.name} ({u.email})</span>
                    <Button size="sm" variant="ghost" onClick={() => handleUnshare(u.id)} className="h-6 w-6 p-0 text-destructive" data-testid={`unshare-${u.id}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
